import type { FinalRate } from '@bip/api-client';
import type { Metadata } from 'next';
import { isVatTax } from '@bip/shared';

// Build-time constant — same value on server and client, safe to use anywhere.
const _rawPrefix = process.env.NEXT_PUBLIC_ASSET_PREFIX || '';
export const PROXY_PREFIX = _rawPrefix === '/' ? '' : _rawPrefix;

/**
 * CMS-parity page metadata for every hotels page. Title is `Bookitplease - Compare Hotel Deals & Save
 * More | <pageName>` set as an ABSOLUTE title so it bypasses the root layout's `%s | BookItPlease`
 * template (which would otherwise append " | BookItPlease"). The description is the SAME on every page.
 * Page names mirror the CMS: home `hotels`, listing `Search Results`, detail `{name}-{city}-{state}-
 * {country}`, guest `Guest Detail`, payment `Payment`.
 */
export const HOTELS_META_DESCRIPTION =
  'Get exclusive hotel deals with Bookitplease. Search, compare, and book accommodations in minutes.';

export function hotelsPageMeta(pageName: string): Metadata {
  return {
    title: { absolute: `Bookitplease - Compare Hotel Deals & Save More | ${pageName}` },
    description: HOTELS_META_DESCRIPTION,
  };
}

/**
 * Resolve the user-currency display price for a property/rate, used identically by
 * the search-results card (`HotelCard`) and the detail "Recommended" headline
 * (`RecommendedPrice`) so the two ALWAYS agree. Prefers the backend-converted
 * user-currency total when present; otherwise converts the property-currency total
 * client-side. Promo is promo-aware and only "active" when it's strictly cheaper.
 *
 * `convertPrice`/`currencyCode` come from `useCurrency()` — passed in so this stays a
 * pure function usable from any component.
 */
export function resolveDisplayPrice(
  finalRate: Pick<FinalRate, 'priceWithBipCommission' | 'currency' | 'totalPromotionalPriceWithBipCommission'>,
  totalPriceInUserCurrency: number | undefined,
  userCurrency: string | undefined,
  convertPrice: (amount: number, fromCurrency: string) => number,
  currencyCode: string,
): { total: number; promo?: number; hasPromo: boolean } {
  // DISPLAY basis: `priceWithBipCommission` (transaction/cost commission EXCLUDED) — "Price with BIP
  // commission incl tax". `totalPriceInUserCurrency` is populated from the SAME basis upstream
  // (ResponseBuilderService/PropertyDetailService), so this stays consistent whichever branch fires.
  const rawTotal = totalPriceInUserCurrency || finalRate.priceWithBipCommission;
  const rawCurrency = totalPriceInUserCurrency ? (userCurrency || currencyCode) : finalRate.currency;
  const total = convertPrice(rawTotal, rawCurrency);
  const promoConverted = finalRate.totalPromotionalPriceWithBipCommission
    ? convertPrice(finalRate.totalPromotionalPriceWithBipCommission, finalRate.currency)
    : undefined;
  const hasPromo = promoConverted !== undefined && promoConverted < total;
  return { total, promo: hasPromo ? promoConverted : undefined, hasPromo };
}

/**
 * CMS featured-amenities selection — 1:1 port of `generateAmenitiesHtml` in the CMS
 * `detail-page-data.js` (BCP-1197). The CMS does NOT show the first-N amenities; it
 * renders a FIXED, ordered set of canonical amenities — whichever of these the
 * property actually has, in this exact priority order, each with its own icon +
 * display label. We use the same list on the property detail page AND the guest
 * "Property details" section so the two surfaces always show the identical set.
 *
 * Matching mirrors the CMS exact-name match (case-insensitive + trimmed for safety),
 * against the property's canonical `amenity_groups[].amenities` — the same source the
 * cart's `amenities[]` is flattened from, so detail page and guest page agree.
 *
 * Icons are Font-Awesome FREE-tier solid glyphs (the CMS uses Pro variants like
 * `fa-paw-simple` / `fa-sharp` which won't render on our kit) — same visual intent.
 */
export const FEATURED_AMENITIES: ReadonlyArray<{ name: string; icon: string; label: string }> = [
  { name: 'Wi-Fi', icon: 'fa-solid fa-wifi', label: 'WiFi' },
  { name: 'Pets Allowed', icon: 'fa-solid fa-paw', label: 'Pet Friendly' },
  { name: 'Outdoor Swimming Pool', icon: 'fa-solid fa-person-swimming', label: 'Outdoor Swimming Pool' },
  { name: 'Bar', icon: 'fa-solid fa-martini-glass', label: 'Bar' },
  { name: '24-Hour Front Desk', icon: 'fa-solid fa-clock', label: '24-Hour Front Desk' },
  { name: 'Gym', icon: 'fa-solid fa-dumbbell', label: 'Gym' },
];

/**
 * Pick the CMS featured amenities present in a flat list of amenity names, returning
 * them in the fixed priority order above with their icon + label. Used by both the
 * detail page (`detail.amenityGroups.flatMap(g => g.amenities)`) and the guest page
 * (`cart.amenities`) so both render exactly the same set.
 */
export function pickFeaturedAmenities(
  amenityNames: readonly string[] | undefined | null,
): Array<{ icon: string; label: string }> {
  const present = new Set((amenityNames ?? []).map((a) => String(a).trim().toLowerCase()));
  return FEATURED_AMENITIES
    .filter((a) => present.has(a.name.toLowerCase()))
    .map(({ icon, label }) => ({ icon, label }));
}

/** @deprecated Use PROXY_PREFIX constant directly. */
export function getProxyPrefix(): string { return PROXY_PREFIX; }

export const HOTELS_API_BASE_URL =
  typeof window === 'undefined'
    ? process.env.HOTELS_SERVICE_URL!
    : process.env.NEXT_PUBLIC_HOTELS_API_URL!;

// ── Suggestion engine switch (MongoDB default; Elasticsearch parallel) ────────
// MongoDB is the LIVE default. Elasticsearch is an opt-in parallel engine for testing.
// Build-time default via NEXT_PUBLIC_SUGGESTION_ENGINE=es; per-request override via a
// `?engine=es` (or `?engine=mongo`) URL param so a single panel / side-by-side can be tested
// without a rebuild. When ES is validated, flip the default and delete the Mongo suggestion path.
export type SuggestionEngine = 'mongo' | 'es';

export const SUGGESTION_ENGINE_DEFAULT: SuggestionEngine =
  process.env.NEXT_PUBLIC_SUGGESTION_ENGINE === 'es' ? 'es' : 'mongo';

/** Resolve the active engine: an explicit `?engine=` URL param wins, else the build-time default. */
export function resolveSuggestionEngine(engineParam?: string | null): SuggestionEngine {
  if (engineParam === 'es') return 'es';
  if (engineParam === 'mongo') return 'mongo';
  return SUGGESTION_ENGINE_DEFAULT;
}

export function buildDefaultDates(): { checkin: string; checkout: string } {
  // Default check-in = TODAY, check-out = TOMORROW. Format with LOCAL getters (never
  // toISOString(), which converts to UTC and shifts the date by a day around midnight in
  // any non-UTC timezone — the source of the "opens on tomorrow's date" incognito bug).
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const checkinDate = new Date();
  const checkoutDate = new Date();
  checkoutDate.setDate(checkoutDate.getDate() + 1);
  return { checkin: fmt(checkinDate), checkout: fmt(checkoutDate) };
}

/**
 * UTC-formatted date-only core (`D MMM'YY`), shared by every funnel surface that needs this exact
 * format — `formatFreeCancelDate` below appends a time suffix; `PaymentSchedule.tsx`'s
 * `formatScheduleDate` (BCP-1843) appends its own. Returns the raw string unchanged if it doesn't
 * parse as a valid date.
 */
export function formatDateDMY(isoString: string): string {
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]}'${String(d.getUTCFullYear()).slice(2)}`;
}

export function formatFreeCancelDate(isoString: string): string {
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  const utcH = d.getUTCHours();
  const h12 = utcH % 12 || 12;
  const ampm = utcH < 12 ? 'am' : 'pm';
  return `${formatDateDMY(isoString)} ${String(h12).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')} ${ampm}`;
}

export function formatDisplayDate(isoDate: string): string {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function nightsBetween(checkin: string, checkout: string): number {
  const ms = new Date(checkout).getTime() - new Date(checkin).getTime();
  return Math.max(1, Math.round(ms / 86_400_000));
}

// Room with per-child ages for accurate pricing
export interface Room {
  adults: number;
  children: number;
  childAges: number[];
}

/** Serialize occupancy rooms array to backend guest string format.
 *  Format per room: `adults` or `adults_age1_age2...` separated by commas.
 */
export function serializeGuests(rooms: Room[]): string {
  return rooms
    .map(({ adults, childAges }) => {
      if (childAges.length === 0) return String(adults);
      return `${adults}_${childAges.join('_')}`;
    })
    .join(',');
}

/** Parse backend guest string back into Room array. e.g. "2,1_5_10" → [{adults:2,children:0,childAges:[]},{adults:1,children:2,childAges:[5,10]}] */
export function parseGuests(guests: string): Room[] {
  if (!guests) return [{ adults: 2, children: 0, childAges: [] }];
  return guests.split(',').map((room) => {
    const parts = room.split('_').map(Number);
    const adults = parts[0] || 2;
    const childAges = parts.slice(1).filter((n) => !isNaN(n));
    return { adults, children: childAges.length, childAges };
  });
}

// ─── CMS-compatible occupancy format ────────────────────────────────────────
// URL: rooms=2&adults=2,3&children=1_5,1_10,2_7
// adults = comma-separated count per room
// children = roomNum_age pairs, comma-separated

/** Serialize Room[] → { rooms, adults, children } for URL params (matches old CMS format) */
export function serializeOccupancyCMS(rooms: Room[]): { rooms: number; adults: string; children: string } {
  const adults = rooms.map((r) => r.adults).join(',');
  const childrenParts: string[] = [];
  rooms.forEach((room, i) => {
    room.childAges.forEach((age) => childrenParts.push(`${i + 1}_${age}`));
  });
  return { rooms: rooms.length, adults, children: childrenParts.join(',') };
}

/**
 * Parse CMS URL params (rooms, adults, children) back into Room[].
 * NOTE: the backend has a parallel parser, `parseCmsOccupancy(adults, children)` in
 * `services/hotels-service/src/common/utils/guest-parser.ts`, used by ContextBuilderService +
 * PropertyDetailService. The two MUST stay in sync — same per-room `adults` count + roomNum_age
 * child grammar — or the backend room count diverges from the UI (the 3-rooms→4 class of bug).
 * Edit both when the occupancy URL grammar changes.
 */
export function parseOccupancyCMS(
  roomsParam: string | number,
  adultsParam: string,
  childrenParam: string,
): Room[] {
  const count = Math.max(1, parseInt(String(roomsParam)) || 1);
  const adultsArr = adultsParam ? adultsParam.split(',') : [];
  const childEntries = childrenParam ? childrenParam.split(',').filter(Boolean) : [];
  return Array.from({ length: count }, (_, i) => {
    const adults = parseInt(adultsArr[i]) || 2;
    const childAges = childEntries
      .filter((e) => e.startsWith(`${i + 1}_`))
      .map((e) => parseInt(e.split('_')[1]));
    return { adults, children: childAges.length, childAges };
  });
}

/** Convert CMS occupancy params into the backend guests string */
export function cmsOccupancyToGuests(
  roomsParam: string,
  adultsParam: string,
  childrenParam: string,
): string {
  return serializeGuests(parseOccupancyCMS(roomsParam, adultsParam, childrenParam));
}

/** Bump a past date forward: checkin → today, checkout → tomorrow (mirrors old CMS setBookingDate logic) */
export function bumpPastDates(checkin: string, checkout: string): { checkin: string; checkout: string } {
  // Local-date format (NOT toISOString — see buildDefaultDates: UTC conversion shifts the day).
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const ci = new Date(checkin + 'T00:00:00');
  const co = new Date(checkout + 'T00:00:00');
  const newCi = ci < today ? today : ci;
  const newCo = co <= newCi ? new Date(newCi.getTime() + 86_400_000) : co;
  return { checkin: fmt(newCi), checkout: fmt(newCo) };
}

/** Unwrap Next.js searchParams value (may be string | string[] | undefined). */
export function spParam(v: string | string[] | undefined): string | undefined {
  if (Array.isArray(v)) return v[0];
  return v;
}

/** Build a URL query string from a params object, skipping empty values. */
export function buildQueryString(params: Record<string, string>): string {
  return Object.entries(params)
    .filter(([, v]) => v != null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
}

export const MULTI_ROOM_SOURCES: readonly string[] = ['BookingClap', 'HyperGuest'];

export interface SearchStage { label: string; duration: number; }

// Used when the user changes a filter or sort option — backend applies filters on cached data.
export const FILTER_STAGES: SearchStage[] = [
  { label: 'Sorting results', duration: 700 },
  { label: 'Finalising results', duration: 700 },
];

// Used when a search returns quickly from cache (~1.5 s total).
export const CACHE_STAGES: SearchStage[] = [
  { label: 'Initiating search', duration: 300 },
  { label: 'Fetching best rates and availability', duration: 400 },
  { label: 'Fetching property details', duration: 350 },
  { label: 'Sorting results', duration: 250 },
  { label: 'Finalising results', duration: 200 },
];

// Used for the loading skeleton during route transitions (before SSE connects).
export const LIVE_SEARCH_STAGES: SearchStage[] = [
  { label: 'Initiating search', duration: 1000 },
  { label: 'Fetching best rates and availability', duration: 4500 },
  { label: 'Fetching property details', duration: 2500 },
  { label: 'Sorting results', duration: 1500 },
  { label: 'Finalising results', duration: 1000 },
];

// Maps backend SSE progress events to human-readable stage labels.
export const SSE_STAGE_LABELS: Record<string, string> = {
  wave1_started: 'Searching global hotel inventory...',
  mapping: 'Fetching property details...',
  finalizing: 'Sorting results...',
  // 9mi / 19mi tiers removed backend-side (direct to 31mi) — these never fire. Kept for re-enable.
  // fallback_9mi:  'Results sparse — searching nearby hotels (9 miles)...',
  // fallback_19mi: 'Expanding search to wider area (19 miles)...',
  fallback_31mi: 'Expanding search to wider area...',
};

// Progress bar percentage for each SSE event (0-92, leaves room for the final jump to 100).
// ORDER MATTERS: the backend emits `wave1_started` → (0–3 `fallback_*` events when results are sparse)
// → `mapping` → `finalizing` → done. So the fallback stages MUST sit BELOW `mapping`/`finalizing` —
// otherwise the bar raced to ~91% during a fallback and then jumped BACK to 55% on `mapping` (the
// reported "bar goes near the end then back to mid" bug). Values now increase in true emission order;
// the consumer also clamps monotonically (never decreases) as a guard against any out-of-order event.
export const SSE_STAGE_PROGRESS: Record<string, number> = {
  wave1_started: 8,
  // 9mi / 19mi fallback tiers were removed backend-side — `getFallbackTiers()` now goes direct to 31mi
  // (rate-hawk.adapter.ts), so these events never fire. Kept (commented) for easy re-enable if the
  // intermediate tiers return; their values would slot in between wave1 and fallback_31mi.
  // fallback_9mi:  24,
  // fallback_19mi: 40,
  fallback_31mi: 55,
  mapping: 68,
  finalizing: 84,
};

export function getStageIndex(stages: SearchStage[], elapsedMs: number): number {
  let idx = stages.length - 1;
  let cumulative = 0;
  for (let i = 0; i < stages.length; i++) {
    cumulative += stages[i].duration;
    if (elapsedMs < cumulative) { idx = i; break; }
  }
  return idx;
}

/**
 * Fallback formatter: strips the middle state/region segment from a full destination label.
 * e.g. "London, Greater London, United Kingdom" → "London, United Kingdom"
 * Used only when the DB does not return countryCode directly.
 */
export function shortDestLabel(label: string): string {
  if (!label) return label;
  const parts = label.split(', ').map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 2) return label;
  return `${parts[0]}, ${parts[parts.length - 1]}`;
}

export function summarizeOccupancy(rooms: Room[]): string {
  const totalAdults = rooms.reduce((s, r) => s + r.adults, 0);
  const totalChildren = rooms.reduce((s, r) => s + r.children, 0);
  // CMS shows the children count ALWAYS, even when 0 ("4 Adults, 0 Children, 2 Rooms" —
  // search_cms.png). Drives the occupancy summary on all 4 search-panel instances via OccupancyPicker.
  const parts: string[] = [
    `${totalAdults} Adult${totalAdults !== 1 ? 's' : ''}`,
    `${totalChildren} Child${totalChildren !== 1 ? 'ren' : ''}`,
    `${rooms.length} Room${rooms.length !== 1 ? 's' : ''}`,
  ];
  return parts.join(', ');
}

/** A single tax line (name + amount in a currency + included/not-included status). */
export interface TaxLine {
  name: string;
  amount: number;
  currency: string;
  status?: string;
}

/**
 * Aggregate per-room taxes into a single "global" tax list for the recommended-block Total Price
 * (i) tooltip (P2-16, CMS HG globalTaxListHTML). Sums each room's taxes × its occurrence, keyed by
 * name|currency|status. Only HG itemises taxes, so this is empty for other sources.
 */
export function aggregateRoomTaxes(
  rooms: Array<{ taxes?: TaxLine[]; occurrence?: number }>,
): TaxLine[] {
  const map: Record<string, TaxLine> = {};
  for (const r of rooms ?? []) {
    for (const t of r.taxes ?? []) {
      // VAT is surfaced as its own "Includes X% VAT" line, never as a row in the tax-list (i). The
      // backend keeps VAT in `taxes` for data completeness (CMS parity) — filter it out for display,
      // mirroring the CMS tax-list filter. (Sidebar/confirmation use `excludeVatTaxes`; same intent.)
      if (isVatTax(t)) continue;
      const amount = t.amount * (r.occurrence ?? 1);
      const key = `${t.name}|${t.currency}|${t.status ?? ''}`;
      if (map[key]) map[key].amount += amount;
      else map[key] = { ...t, amount };
    }
  }
  return Object.values(map);
}

/**
 * Validated mask length from a `bip_l` URL param — a positive integer, else `0` (covers absent,
 * `"0"`, and non-numeric values uniformly). Single source of truth for every `bip_k`/`bip_l`-masked
 * ratecode consumer (search/detail pages, SearchBar) — the plugin embed's own landing page derives
 * its own mask independently, see booking-engine-landing/settings.ts.
 */
export function parseBipMaskLength(bipL?: string): number {
  const length = bipL ? parseInt(bipL, 10) : 0;
  return Number.isInteger(length) && length > 0 ? length : 0;
}

/**
 * Visual mask string ('X'×length) for a masked affiliate ratecode. Returns `undefined` (not `''`)
 * whenever there's nothing meaningful to mask, so callers can safely `?? realRatecode` without an
 * empty string silently winning over a real fallback value (nullish coalescing doesn't trap `''`).
 */
export function buildMaskedRatecodeDisplay(bipK?: string, bipL?: string): string | undefined {
  if (!bipK) return undefined;
  const length = parseBipMaskLength(bipL);
  return length > 0 ? 'X'.repeat(length) : undefined;
}
